import type { CoverageMatrix, Environment, TileState } from "@csbi/contracts"
import { DOMAIN_LABELS, type MetricDomain } from "@csbi/metric-catalog"

/**
 * Fetching and filtering for the coverage matrix.
 *
 * All pure functions, **no React** — "which metrics count as not wired up" is an acceptance definition, not a
 * rendering detail. It has to match `metricsLinked` in the backend's `repo/coverage.ts` word for word (at least
 * one customer `ok`), or the `78/90` at the top and the row count below it disagree, **both on the same screen**.
 */

/** The four perspectives on a row. This order is the order of the segmented control */
export const STATE_FILTERS = ["all", "unlinked", "unsupported", "failing"] as const
export type StateFilter = (typeof STATE_FILTERS)[number]

export const STATE_FILTER_LABELS: Record<StateFilter, string> = {
  all: "All metrics",
  unlinked: "Not linked",
  unsupported: "Unsupported everywhere",
  failing: "Collection failing",
}

export type MatrixMetric = CoverageMatrix["metrics"][number]

export type MatrixRow = MatrixMetric & {
  domainLabel: string
  /** How many customers are ok. The denominator is the column count, not the total customer count — a session's visible scope may be narrower */
  okCount: number
}

/**
 * Row counts per perspective. **Computed before filtering**, or the numbers on the segmented control would move
 * with the current filter and mean nothing ("Not linked (12)" has to equal the number of rows you see after clicking it).
 *
 * Note that `unsupported` is a **subset** of `unlinked`: unsupported everywhere means no ok anywhere.
 * They are listed separately because the next action differs — one means check collection, the other means wait for an srv release.
 */
export function stateCounts(matrix: CoverageMatrix | undefined): Record<StateFilter, number> {
  const metrics = matrix?.metrics ?? []
  return {
    all: metrics.length,
    unlinked: metrics.filter(isUnlinked).length,
    unsupported: metrics.filter(isUnsupportedEverywhere).length,
    failing: metrics.filter(isFailing).length,
  }
}

/** No customer is ok — the complement of `summary.metricsLinked`, by the same definition */
export const isUnlinked = (m: MatrixMetric) => !m.cells.includes("ok")

/** Every column is unsupported. An empty list does not count (that is "no customers yet", not "unsupported everywhere") */
export const isUnsupportedEverywhere = (m: MatrixMetric) =>
  m.cells.length > 0 && m.cells.every((c) => c === "unsupported")

/** At least one customer has failed collection repeatedly. An operations problem, unrelated to whether the metric itself works */
export const isFailing = (m: MatrixMetric) => m.cells.includes("stale")

const MATCHES: Record<StateFilter, (m: MatrixMetric) => boolean> = {
  all: () => true,
  unlinked: isUnlinked,
  unsupported: isUnsupportedEverywhere,
  failing: isFailing,
}

export const PRIORITIES = ["must", "nice", "unspecified"] as const
export type Priority = (typeof PRIORITIES)[number]

/**
 * Select columns by environment, **projecting each metric's `cells` onto the columns that survive**.
 *
 * Projection rather than "skip a few columns while rendering", because every downstream number is computed per
 * column: the `3/6` at the end of a row, the column totals in the footer, the `Not linked (12)` on the segmented
 * control. Skipping only at the render layer would leave a row showing `/6` after filtering out 4 dev deployments while the screen has 2 columns — a denominator pointing at something no longer on screen.
 *
 * An empty selection means no filter (not "no environments at all").
 */
export function projectColumns(
  matrix: CoverageMatrix | undefined,
  environments: readonly Environment[],
): CoverageMatrix | undefined {
  if (!matrix || environments.length === 0) return matrix
  const keep = matrix.tenants.map((t, i) => [t, i] as const).filter(([t]) => environments.includes(t.environment))
  return {
    ...matrix,
    tenants: keep.map(([t]) => t),
    metrics: matrix.metrics.map((m) => ({ ...m, cells: keep.map(([, i]) => m.cells[i]!).filter(Boolean) })),
    // summary is untouched: the top row is the **full** acceptance decision and must not be rewritten by "I only want to look at production right now"
  }
}

/** The environments present in the data, in the contract's fixed order — a dropdown's order should not follow registration order */
export function environmentsInMatrix(matrix: CoverageMatrix | undefined): Environment[] {
  const order: Environment[] = ["production", "staging", "dev"]
  const present = new Set(matrix?.tenants.map((t) => t.environment) ?? [])
  return order.filter((e) => present.has(e))
}

export type MatrixFilters = {
  search: string
  domains: readonly MetricDomain[]
  priorities: readonly Priority[]
  state: StateFilter
}

/**
 * Select the rows to display. **Keeps the order the API returned**: that is the catalog's order, which is rows
 * 1–90 of the dictionary, and the catalog itself is grouped by domain. So not sorting gives blocks by domain,
 * ordered by dictionary row within each — the same order as on `/domain/:slug`. Sorting again would only make the two disagree.
 */
export function matrixRows(matrix: CoverageMatrix | undefined, filters: MatrixFilters): MatrixRow[] {
  if (!matrix) return []
  const needle = filters.search.trim().toLowerCase()
  const domains = new Set<string>(filters.domains)
  const matches = MATCHES[filters.state]

  const priorities = new Set<string>(filters.priorities)

  return matrix.metrics
    .filter((m) => matches(m))
    .filter((m) => domains.size === 0 || domains.has(m.domain))
    .filter((m) => priorities.size === 0 || priorities.has(m.priority))
    .filter(
      (m) =>
        needle === "" ||
        m.name.toLowerCase().includes(needle) ||
        // The id takes part in matching too: when investigating, what you have in hand is usually `last_login_date` rather than "Last Login Date"
        m.id.toLowerCase().includes(needle),
    )
    .map((m) => ({
      ...m,
      domainLabel: DOMAIN_LABELS[m.domain as MetricDomain] ?? m.domain,
      okCount: m.cells.filter((c) => c === "ok").length,
    }))
}

/** The domains present in the data, in the catalog's domain order — the dropdown order should match the blocks in the table */
export function domainsInMatrix(matrix: CoverageMatrix | undefined): MetricDomain[] {
  const seen = new Set<string>()
  const out: MetricDomain[] = []
  for (const m of matrix?.metrics ?? []) {
    if (seen.has(m.domain)) continue
    seen.add(m.domain)
    out.push(m.domain as MetricDomain)
  }
  return out
}

/**
 * The four-state distribution of one column (one customer). Used by the footer total row.
 *
 * A column total says more than a row total: one column suddenly showing 60 empties means **that deployment's
 * collection is broken**, while row by row you would only see 60 rows each "missing a customer", and nobody connects them.
 */
export function columnTotals(rows: readonly MatrixRow[], tenantCount: number): Record<TileState, number>[] {
  return Array.from({ length: tenantCount }, (_, i) => {
    const totals: Record<TileState, number> = { ok: 0, empty: 0, unsupported: 0, stale: 0 }
    for (const r of rows) {
      const state = r.cells[i]
      if (state) totals[state] += 1
    }
    return totals
  })
}
